/**
 * Math expression parser utility for handling special mathematical expressions
 * like square roots (√) and other common operators in voltage ratio calculations.
 */


const MATH_CONSTANTS = {
    "π": Math.PI,
    "pi": Math.PI,
    "e": Math.E,
    "√2": Math.SQRT2,
    "√3": Math.sqrt(3),  
    "R3": Math.sqrt(3)
  };
  
  /**
   * Parses a mathematical expression string, including special symbols like √ (square root)
   * @param {string} expression - The mathematical expression to parse
   * @returns {number} - The calculated numerical result
   */
  export const parseExpression = (expression) => {
    if (!expression || typeof expression !== 'string') {
      return NaN;
    }
    
    let processedExpression = expression.trim();
    
    
    Object.entries(MATH_CONSTANTS).forEach(([symbol, value]) => {
      processedExpression = processedExpression.replace(new RegExp(symbol, 'g'), value);
    });
    
    
    processedExpression = processedExpression.replace(/√(\d+(\.\d+)?)/g, (_, number) => {
      return Math.sqrt(parseFloat(number));
    });
    
    try {
      
      return Function(`'use strict'; return (${processedExpression});`)();
    } catch (error) {
      console.error("Error parsing expression:", expression, error);
      
      return parseFloat(expression);
    }
  };
  
  /**
   * Formats a number to a readable string representation
   * @param {number} value - The number to format
   * @param {number} precision - Number of decimal places (default: 3)
   * @returns {string} - Formatted string representation of the number
   */
  export const formatNumber = (value, precision = 3) => {
    if (isNaN(value) || value === null || value === undefined) {
      return "N/A";
    }
    return value.toFixed(precision);
  };
  
  /**
   * Calculates the voltage ratio between primary and secondary values
   * @param {string} primary - Primary voltage expression (e.g., "33000/√3")
   * @param {string} secondary - Secondary voltage expression (e.g., "110/√3")
   * @returns {number|null} - Calculated ratio or null if invalid inputs
   */
  export const calculateRatio = (primary, secondary) => {
    const primaryValue = parseExpression(primary);
    const secondaryValue = parseExpression(secondary);
    
    if (isNaN(primaryValue) || isNaN(secondaryValue) || primaryValue === 0) {
      return null;
    }
    
    return secondaryValue / primaryValue;
  };
  
  /**
   * Calculates the expected secondary voltage based on primary voltage and ratio
   * @param {string} primary - Primary voltage expression
   * @param {string} secondary - Secondary voltage expression
   * @param {number} appliedVoltage - Applied voltage value
   * @returns {number|null} - Expected secondary voltage or null if invalid inputs
   */
  export const calculateExpectedSecondary = (primary, secondary, appliedVoltage) => {
    const ratio = calculateRatio(primary, secondary);
    
    if (ratio === null || isNaN(appliedVoltage)) {
      return null;
    }
    
    return ratio * appliedVoltage;
  };
  
  /**
   * Calculates the error percentage between measured and expected values
   * @param {number} measured - Measured value
   * @param {number} expected - Expected value
   * @returns {number|null} - Error percentage or null if invalid inputs
   */
  export const calculateErrorPercentage = (measured, expected) => {
    if (isNaN(measured) || isNaN(expected) || expected === 0) {
      return null;
    }
    
    return Math.abs(((measured - expected) / expected) * 100);
  };